/**
 * File: leadsService.ts
 * Path: apps/admin/src/shared/services/leadsService.ts
 * Description: Every read and write the Leads pages make. Hooks call these;
 * nothing else in the feature touches Firestore or the search function, so
 * tests can swap this one module.
 *
 * Writes that change a lead and record why (a stage move, a conversion) go in
 * one batch or transaction, so the history can't disagree with the lead. The write side takes
 * its Firestore instance from a getter so the rules tests can run these exact
 * batches against the emulator as a signed-in admin.
 */

import { resolveHttpFunctionUrl } from "@idk/config/firebase/functionsUrl";
import {
  auth,
  BUSINESSES_COLLECTION,
  businessFolderName,
  clientFromLead,
  consentWrite,
  db,
  describeConsent,
  FIRESTORE_COLLECTIONS,
  FirestoreService,
  FOLLOW_UP_CALLS,
  formatPhone,
  FunctionsService,
  LEADS_PIPELINE_LIMIT,
  leadFromCandidate,
  LEAD_SOURCE_LABELS,
  leadIdForPlace,
  limit,
  LEAD_CONTACT_LABELS,
  nextCallAfterNoAnswer,
  orderBy,
  stageAfterQuote,
  storage,
  stageDateFields,
  toLead,
  todayInSouthAfrica,
  toLeadActivity,
  where,
  type Consent,
  type DraftLeadEmailRequest,
  type DraftLeadEmailResponse,
  type FindMapLeadsRequest,
  type FindMapLeadsResponse,
  type Lead,
  type LeadActivity,
  type LeadActivityType,
  type LeadContactType,
  type LeadDetails,
  type LeadOrigin,
  type LeadStage,
  type MapLeadCandidate,
} from "@idk/config";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Firestore,
  type Transaction,
} from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";
import { claimClientNumber, readClientCounter } from "./clientNumberingService";
import { recordHome } from "./businessesService";
import { leadsUnion } from "../utils/businessRecordUtils";
import { toAuditRun, type AuditRun } from "../utils/auditHistoryUtils";

const LEADS = FIRESTORE_COLLECTIONS.leads;
// The admin has no hosting rewrites, so functions are called by full URL.
const LEAD_AUDIT_PDF_URL = resolveHttpFunctionUrl("leadAuditPdf", { sameOrigin: false });
const CLIENTS = FIRESTORE_COLLECTIONS.clients;
const BUSINESSES = BUSINESSES_COLLECTION;
/** Leads per parking batch: two writes each, well under the 500 limit. */
const PARK_CHUNK = 200;

/**
 * Where a record lives is decided per record: its business when it has one,
 * the old lead document until the move has reached it. `home` is that
 * collection's name.
 */
const activityPath = (home: string, leadId: string) => `${home}/${leadId}/activity`;
/** Far above a day's history entries across every lead and client. */
const OUTREACH_READ_LIMIT = 1000;
const auditsPath = (home: string, leadId: string) => `${home}/${leadId}/audits`;

/**
 * What every new record carries in the one-record layout: where it came from
 * (nothing: it was never a lead or client document) and its Storage folder,
 * fixed now (businessFolderName).
 */
export const newBusinessFields = (id: string, name: string) => ({
  legacy: { leadId: null, clientId: null },
  folder: businessFolderName({ id, name: name || "business", clientNumber: null }),
});

/** Who is acting, stamped on every history entry. */
export interface Actor {
  uid: string;
  email: string;
}

type Stored = Record<string, unknown> & { id: string };

/** What a stage move needs to know about the lead it moves. */
type MovableLead = Pick<Lead, "id" | "stage" | "contactedAt" | "talkingAt" | "quotedAt">;

const NO_STAGE_DATES = { contactedAt: null, talkingAt: null, quotedAt: null };

const activityEntry = (
  actor: Actor,
  type: LeadActivityType,
  fields: { text?: string; from?: LeadStage; to?: LeadStage } = {},
) => ({
  type,
  text: fields.text ?? "",
  from: fields.from ?? null,
  to: fields.to ?? null,
  byUid: actor.uid,
  byEmail: actor.email,
  createdAt: serverTimestamp(),
});

/** Removes one Storage file; one already gone counts as removed. */
const deleteStoredFile = async (path: string): Promise<void> => {
  try {
    await deleteObject(storageRef(storage, path));
  } catch (error) {
    if ((error as { code?: string }).code !== "storage/object-not-found") throw error;
  }
};

export const createLeadsRepo = (
  getDb: () => Firestore,
  removeFile: (path: string) => Promise<void> = deleteStoredFile,
) => {
  const newActivityRef = (home: string, leadId: string) => doc(collection(getDb(), activityPath(home, leadId)));
  const homeOf = (leadId: string) => recordHome(getDb(), leadId, "leads");

  /** A stage move's writes: the lead and its history entry, in one transaction. */
  const writeStageMove = (
    tx: Transaction,
    home: string,
    lead: MovableLead,
    to: LeadStage,
    actor: Actor,
    text: string,
    lostReason = "",
  ) => {
    tx.update(doc(getDb(), home, lead.id), {
      stage: to,
      lostReason: to === "lost" ? lostReason.trim() : "",
      stageChangedAt: serverTimestamp(),
      // When it got here: wonAt / lostAt while it is there, first times for the rest.
      ...stageDateFields(lead, to, serverTimestamp()),
      updatedAt: serverTimestamp(),
    });
    tx.set(newActivityRef(home, lead.id), activityEntry(actor, "stage", { from: lead.stage, to, text }));
  };

  /** A first message to the lead: logged, follow-up set, New moved to Contacted. */
  const recordFirstMessage = async (
    lead: Lead,
    note: string,
    followUp: string,
    actor: Actor,
    how: string,
  ): Promise<void> => {
    const home = await homeOf(lead.id);
    const batch = writeBatch(getDb());
    const moves = lead.stage === "new";
    // Asking is the one approach POPIA allows; it is recorded so a second can be refused.
    const asking = lead.consent.state === "not_asked";
    batch.update(doc(getDb(), home, lead.id), {
      openerSentAt: serverTimestamp(),
      ...(asking ? { consent: { state: "asked", on: todayInSouthAfrica(), how } } : {}),
      // The follow-up calls start again from this message (followUps.ts).
      followUpCalls: 0,
      nextFollowUp: followUp,
      nextActionType: "call",
      updatedAt: serverTimestamp(),
      ...(moves
        ? {
            stage: "contacted",
            stageChangedAt: serverTimestamp(),
            ...stageDateFields(lead, "contacted", serverTimestamp()),
          }
        : {}),
    });
    if (moves) {
      batch.set(newActivityRef(home, lead.id), activityEntry(actor, "stage", { from: "new", to: "contacted" }));
    }
    batch.set(newActivityRef(home, lead.id), activityEntry(actor, "note", { text: note }));
    await batch.commit();
  };

  /**
     * Deletes a lead with its history and saved audits together (the rules
     * allow nothing else), including the audit screenshots kept in Storage.
     */
  const removeLead = async (leadId: string): Promise<void> => {
    const home = await homeOf(leadId);
    const history = await getDocs(collection(getDb(), activityPath(home, leadId)));
    const audits = await getDocs(collection(getDb(), auditsPath(home, leadId)));
    // A business also holds the client's jobs; they go with it.
    const jobs = home === BUSINESSES ? await getDocs(collection(getDb(), `${home}/${leadId}/jobs`)) : null;
    const shots = await Promise.all(
      audits.docs.map((audit) => getDocs(collection(audit.ref, "shots"))),
    );
    // Time and rand costs logged against the lead go with it.
    const effort = await Promise.all(
      [FIRESTORE_COLLECTIONS.timeEntries, FIRESTORE_COLLECTIONS.costEntries].map((name) =>
        getDocs(
          query(collection(getDb(), name), where("recordKind", "==", "lead"), where("recordId", "==", leadId)),
        ),
      ),
    );
    // Screenshots go first: if one fails the record is still there to try again.
    const imagePaths = shots.flatMap((set) =>
      set.docs.flatMap((shot) => {
        const path = (shot.data() as { path?: unknown }).path;
        return typeof path === "string" && path ? [path] : [];
      }),
    );
    await Promise.all(imagePaths.map(removeFile));
    const batch = writeBatch(getDb());
    effort.forEach((set) => set.docs.forEach((entry) => batch.delete(entry.ref)));
    jobs?.docs.forEach((job) => batch.delete(job.ref));
    history.docs.forEach((entry) => batch.delete(entry.ref));
    shots.forEach((set) => set.docs.forEach((shot) => batch.delete(shot.ref)));
    audits.docs.forEach((audit) => batch.delete(audit.ref));
    batch.delete(doc(getDb(), home, leadId));
    await batch.commit();
  };

  return {
    /** Searches Google Maps through the admin-only server function. */
    findOnMaps: (request: FindMapLeadsRequest) =>
      FunctionsService.call<FindMapLeadsRequest, FindMapLeadsResponse>("findMapLeads", request),

    /** The union of businesses (before "won") and old leads no business stands for. */
    list: async (): Promise<Lead[]> => {
      const [businesses, oldLeads] = await Promise.all([
        FirestoreService.query<Stored>(BUSINESSES, orderBy("updatedAt", "desc"), limit(LEADS_PIPELINE_LIMIT * 2)),
        FirestoreService.query<Stored>(LEADS, orderBy("updatedAt", "desc"), limit(LEADS_PIPELINE_LIMIT)),
      ]);
      return leadsUnion(businesses, oldLeads);
    },

    /** The business when there is one, else the old lead. */
    get: async (id: string): Promise<Lead | null> => {
      const row =
        (await FirestoreService.getById<Stored>(BUSINESSES, id)) ??
        (await FirestoreService.getById<Stored>(LEADS, id));
      return row ? toLead(row.id, row) : null;
    },

    /** Every audit run saved on the lead, newest first (auditHistory.ts). */
    audits: async (leadId: string): Promise<AuditRun[]> => {
      const rows = await FirestoreService.query<Stored>(
        auditsPath(await homeOf(leadId), leadId),
        orderBy("createdAt", "desc"),
        limit(50),
      );
      return rows.map((row) => toAuditRun(row.id, row));
    },

    /**
     * An audit report from elsewhere was attached (Documents, tagged "audit"):
     * the lead counts as audited from now, and the history says so.
     */
    markAuditUploaded: async (leadId: string, fileName: string, actor: Actor): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, leadId), {
        auditUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      batch.set(newActivityRef(home, leadId), activityEntry(actor, "note", { text: `Audit report attached: ${fileName}` }));
      await batch.commit();
    },

    activity: async (leadId: string): Promise<LeadActivity[]> => {
      const rows = await FirestoreService.query<Stored>(
        activityPath(await homeOf(leadId), leadId),
        orderBy("createdAt", "desc"),
        limit(200),
      );
      return rows.map((row) => toLeadActivity(row.id, row));
    },

    /**
     * Saves Maps businesses that aren't in the pipeline yet. Runs as one
     * transaction, so two people (or two tabs) saving the same business can't
     * both create it; the ones already there are reported back, not touched.
     */
    saveFromMaps: async (
      candidates: MapLeadCandidate[],
      search: { keyword: string; location: string },
      actor: Actor,
    ): Promise<{ saved: string[]; skipped: string[] }> =>
      runTransaction(getDb(), async (tx) => {
        const refs = candidates.map((c) => doc(getDb(), BUSINESSES, leadIdForPlace(c.placeId)));
        const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
        // A business saved before the move still sits in leads: it counts as saved.
        const oldSnaps = await Promise.all(
          candidates.map((c) => tx.get(doc(getDb(), LEADS, leadIdForPlace(c.placeId)))),
        );
        const saved: string[] = [];
        const skipped: string[] = [];

        candidates.forEach((candidate, i) => {
          if (snaps[i].exists() || oldSnaps[i].exists()) {
            skipped.push(candidate.placeId);
            return;
          }
          const lead = leadFromCandidate(candidate, search);
          tx.set(refs[i], {
            ...lead,
            ...newBusinessFields(refs[i].id, lead.name),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            stageChangedAt: serverTimestamp(),
          });
          const where = [search.keyword, search.location].filter(Boolean).join(" in ");
          tx.set(
            newActivityRef(BUSINESSES, refs[i].id),
            activityEntry(actor, "created", { text: `Found on Google Maps searching "${where}"` }),
          );
          saved.push(candidate.placeId);
        });

        return { saved, skipped };
      }),

    /** Adds a lead by hand (a referral, a call, someone met in person). */
    create: async (
      details: LeadDetails,
      origin: LeadOrigin,
      actor: Actor,
    ): Promise<string> => {
      const ref = doc(collection(getDb(), BUSINESSES));
      const batch = writeBatch(getDb());
      batch.set(ref, {
        ...details,
        ...newBusinessFields(ref.id, details.name),
        phone: formatPhone(details.phone),
        source: origin.source,
        affiliateId: origin.source === "referral" ? origin.affiliateId : null,
        stage: "new",
        lostReason: "",
        place: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        stageChangedAt: serverTimestamp(),
      });
      batch.set(
        newActivityRef(BUSINESSES, ref.id),
        activityEntry(actor, "created", {
          text: `Added by hand. Source: ${LEAD_SOURCE_LABELS[origin.source]}.`,
        }),
      );
      await batch.commit();
      return ref.id;
    },

    /**
     * Parks leads for later, or brings them back, with a line in each one's
     * history. Batched in chunks so a long selection stays under Firestore's
     * 500-writes-per-batch limit (two writes per lead).
     */
    setParked: async (leads: readonly Pick<Lead, "id">[], parked: boolean, actor: Actor): Promise<void> => {
      for (let i = 0; i < leads.length; i += PARK_CHUNK) {
        const batch = writeBatch(getDb());
        for (const lead of leads.slice(i, i + PARK_CHUNK)) {
          const home = await homeOf(lead.id);
          batch.update(doc(getDb(), home, lead.id), { parked, updatedAt: serverTimestamp() });
          batch.set(
            newActivityRef(home, lead.id),
            activityEntry(actor, "note", { text: parked ? "Parked for later" : "Back from parked" }),
          );
        }
        await batch.commit();
      }
    },

    update: async (id: string, details: Partial<LeadDetails>) =>
      updateDoc(doc(getDb(), await homeOf(id), id), {
        ...details,
        // Phone numbers are kept in one format, whoever types them.
        ...(details.phone === undefined ? {} : { phone: formatPhone(details.phone) }),
        updatedAt: serverTimestamp(),
      }),

    /** Where the lead came from (and the partner, for a referral), with a line in its history. */
    setOrigin: async (leadId: string, origin: LeadOrigin, actor: Actor): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, leadId), {
        source: origin.source,
        affiliateId: origin.source === "referral" ? origin.affiliateId : null,
        updatedAt: serverTimestamp(),
      });
      batch.set(
        newActivityRef(home, leadId),
        activityEntry(actor, "note", { text: `Source set to ${LEAD_SOURCE_LABELS[origin.source]}` }),
      );
      await batch.commit();
    },

    /**
     * Contact that happened (a call, WhatsApp, email or meeting) and what
     * came of it. Typed, so outreach per day and speed-to-lead can be counted.
     */
    logContact: async (
      leadId: string,
      type: LeadContactType,
      text: string,
      actor: Actor,
    ): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.set(newActivityRef(home, leadId), activityEntry(actor, type, { text: text.trim() }));
      batch.update(doc(getDb(), home, leadId), { updatedAt: serverTimestamp() });
      await batch.commit();
    },

    /**
     * "Move to Won" on a business sets its stage and, once numbering has
     * started, gives it the next client number (C001...) in the same
     * transaction. Nothing is copied: it is the same record. A business
     * that already has a number keeps it (moved back, then won again).
     * A lead that is still an old document keeps the old way: winning it
     * also makes the client document, under the same id.
     */
    moveStage: async (
      lead: Lead,
      to: LeadStage,
      actor: Actor,
      lostReason = "",
      lesson = "",
    ): Promise<void> => {
      if (lead.stage === to) return;
      const home = await homeOf(lead.id);
      const clientRef = doc(getDb(), CLIENTS, lead.id);
      const businessRef = doc(getDb(), BUSINESSES, lead.id);
      await runTransaction(getDb(), async (tx) => {
        const isBusiness = home === BUSINESSES;
        const business = isBusiness ? (await tx.get(businessRef)).data() : undefined;
        const numberIt = isBusiness && to === "won" && typeof business?.clientNumber !== "number";
        const becomesClient = !isBusiness && to === "won" && !(await tx.get(clientRef)).exists();
        const lastClientNumber = numberIt || becomesClient ? await readClientCounter(tx, getDb()) : null;
        writeStageMove(
          tx,
          home,
          lead,
          to,
          actor,
          to === "lost" ? lostReason.trim() : becomesClient ? "Added to clients" : "",
          lostReason,
        );
        if (to === "lost" && lesson.trim()) {
          tx.set(newActivityRef(home, lead.id), activityEntry(actor, "note", { text: `Lesson: ${lesson.trim()}` }));
        }
        if (numberIt) {
          tx.update(businessRef, {
            ...(lastClientNumber === null
              ? {}
              : { clientNumber: claimClientNumber(tx, getDb(), lastClientNumber, lead.id) }),
            ...(typeof business?.since === "string" && business.since ? {} : { since: todayInSouthAfrica() }),
          });
        }
        if (becomesClient) {
          const clientNumber =
            lastClientNumber === null ? null : claimClientNumber(tx, getDb(), lastClientNumber, lead.id);
          tx.set(clientRef, {
            ...clientFromLead(lead, todayInSouthAfrica()),
            ...(clientNumber === null ? {} : { clientNumber }),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      });
    },

    /**
     * Records a WhatsApp opener: the message in the history, a follow-up date,
     * and a New lead moved to Contacted. `openerSentAt` is what stops a second
     * unconsented opener (see model/opener.ts).
     */
    recordOpener: (lead: Lead, text: string, followUp: string, actor: Actor): Promise<void> =>
      recordFirstMessage(lead, `WhatsApp opener sent:\n\n${text.trim()}`, followUp, actor, "WhatsApp opener"),

    /**
     * Records the email that sent the audit, the same way as the opener: it
     * is the one approach POPIA allows without consent (auditEmailBlock).
     */
    recordAuditEmail: (
      lead: Lead,
      email: { subject: string; body: string },
      followUp: string,
      actor: Actor,
    ): Promise<void> =>
      recordFirstMessage(
        lead,
        `Audit email sent to ${lead.email}:\n\nSubject: ${email.subject.trim()}\n\n${email.body.trim()}`,
        followUp,
        actor,
        "Audit email",
      ),

    /**
     * The lead's last audit report, printed again by the server from what was
     * saved: no new scan, same reference and date.
     */
    /** The latest saved report, or an older run's when `auditId` is given. */
    auditPdf: async (leadId: string, auditId?: string): Promise<Blob> => {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(LEAD_AUDIT_PDF_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(auditId ? { leadId, auditId } : { leadId }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || `The report couldn't be printed (${response.status}).`);
      }
      return response.blob();
    },

    /** Gemini's draft of the email that sends the lead their audit. Sends nothing. */
    draftAuditEmail: (leadId: string) =>
      FunctionsService.call<DraftLeadEmailRequest, DraftLeadEmailResponse>("draftLeadEmail", { leadId }),

    /**
     * A follow-up call nobody answered: counted, logged, and the next call
     * put on the plan (none after the last one: the lead is then burnt).
     */
    logUnansweredCall: async (lead: Lead, today: string, actor: Actor): Promise<void> => {
      const number = lead.followUpCalls + 1;
      const next = nextCallAfterNoAnswer(lead, today);
      const home = await homeOf(lead.id);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, lead.id), {
        followUpCalls: number,
        nextFollowUp: next,
        nextActionType: next ? "call" : null,
        updatedAt: serverTimestamp(),
      });
      batch.set(
        newActivityRef(home, lead.id),
        activityEntry(actor, "note", {
          text: `Follow-up call ${number} of ${FOLLOW_UP_CALLS}: no answer.${next ? ` Next call ${next}.` : " That was the last planned call."}`,
        }),
      );
      await batch.commit();
    },

    /**
     * The next step for a lead: what (call, WhatsApp, email, meeting) and
     * which day. Every open lead should always have one (Today page).
     */
    setNextAction: async (
      leadId: string,
      next: { type: LeadContactType; due: string },
      actor: Actor,
    ): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, leadId), {
        nextFollowUp: next.due,
        nextActionType: next.type,
        nextStepTbd: false,
        updatedAt: serverTimestamp(),
      });
      batch.set(
        newActivityRef(home, leadId),
        activityEntry(actor, "note", { text: `Next step: ${LEAD_CONTACT_LABELS[next.type].toLowerCase()} on ${next.due}` }),
      );
      await batch.commit();
    },

    /**
     * The next step has no date yet (they couldn't confirm): the old date is
     * cleared and the lead stays quiet until one is set.
     */
    setNextStepTbd: async (leadId: string, type: LeadContactType, actor: Actor): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, leadId), {
        nextFollowUp: "",
        nextActionType: type,
        nextStepTbd: true,
        updatedAt: serverTimestamp(),
      });
      batch.set(
        newActivityRef(home, leadId),
        activityEntry(actor, "note", { text: `Next step: ${LEAD_CONTACT_LABELS[type].toLowerCase()}, date to be decided` }),
      );
      await batch.commit();
    },

    /**
     * Calls, messages and emails logged since `sinceMs` (the start of today).
     * One collection-group query over every lead's history (client histories
     * share the name and are left out), instead of one query per lead touched
     * today, run one after another. Meetings and notes are not outreach.
     * Until the collection-group index exists it falls back to the old way.
     */
    outreachSince: async (sinceMs: number): Promise<number> => {
      const since = new Date(sinceMs);
      const isOutreach = (data: Record<string, unknown>) => ["call", "whatsapp", "email"].includes(String(data.type));
      const isLeadSide = (parent: string | undefined) => parent === LEADS || parent === BUSINESSES;
      try {
        const entries = await getDocs(
          query(collectionGroup(getDb(), "activity"), where("createdAt", ">=", since), limit(OUTREACH_READ_LIMIT)),
        );
        return entries.docs.filter((e) => isLeadSide(e.ref.parent.parent?.parent.id) && isOutreach(e.data())).length;
      } catch {
        let count = 0;
        for (const home of [LEADS, BUSINESSES]) {
          const touched = await getDocs(
            query(collection(getDb(), home), where("updatedAt", ">=", since), limit(LEADS_PIPELINE_LIMIT)),
          );
          for (const lead of touched.docs) {
            const entries = await getDocs(
              query(collection(getDb(), activityPath(home, lead.id)), where("createdAt", ">=", since), limit(100)),
            );
            count += entries.docs.filter((e) => isOutreach(e.data())).length;
          }
        }
        return count;
      }
    },

    addNote: async (leadId: string, text: string, actor: Actor): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.set(newActivityRef(home, leadId), activityEntry(actor, "note", { text: text.trim() }));
      // Bumps the lead up the "recently active" order.
      batch.update(doc(getDb(), home, leadId), { updatedAt: serverTimestamp() });
      await batch.commit();
    },

    // ─── Inside another feature's transaction (saved quotes) ─────────────

    /** A fresh lead id, for a lead created inside someone else's transaction. */
    newLeadRef: () => doc(collection(getDb(), BUSINESSES)),

    /**
     * Adds a lead by hand inside the caller's transaction, at `stage`, with
     * the same history create() writes (plus the move, when it starts past
     * New). Writes only, so it can follow the caller's reads.
     */
    createInTx: (
      tx: Transaction,
      leadId: string,
      details: LeadDetails,
      actor: Actor,
      fields: { createdText: string; stage?: LeadStage; stageText?: string },
    ): void => {
      const stage = fields.stage ?? "new";
      tx.set(doc(getDb(), BUSINESSES, leadId), {
        ...details,
        ...newBusinessFields(leadId, details.name),
        source: "manual",
        stage,
        lostReason: "",
        place: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        stageChangedAt: serverTimestamp(),
        ...(stage === "new" ? {} : stageDateFields(NO_STAGE_DATES, stage, serverTimestamp())),
      });
      tx.set(newActivityRef(BUSINESSES, leadId), activityEntry(actor, "created", { text: fields.createdText }));
      if (stage !== "new") {
        tx.set(
          newActivityRef(BUSINESSES, leadId),
          activityEntry(actor, "stage", { from: "new", to: stage, text: fields.stageText ?? "" }),
        );
      }
    },

    /**
     * A quote sent to this lead, inside the caller's transaction: a note in
     * its history (linked to the quote) and, for a lead before "quoted",
     * the move to "quoted" (stageAfterQuote). Later stages stay put. A lead
     * created in the same transaction (createInTx) already has its stage,
     * so only the note is written for it.
     */
    recordQuoteInTx: (
      tx: Transaction,
      lead: MovableLead,
      text: string,
      quoteId: string,
      actor: Actor,
      options: { createdInThisTransaction?: boolean; home?: string } = {},
    ): void => {
      // A lead created in this transaction is a business; otherwise the caller says where it was read.
      const home = options.createdInThisTransaction ? BUSINESSES : (options.home ?? LEADS);
      if (!options.createdInThisTransaction) {
        const to = stageAfterQuote(lead.stage);
        if (to !== lead.stage) {
          writeStageMove(tx, home, lead, to, actor, "Quote sent");
        } else {
          tx.update(doc(getDb(), home, lead.id), { updatedAt: serverTimestamp() });
        }
      }
      tx.set(newActivityRef(home, lead.id), { ...activityEntry(actor, "note", { text }), quoteId });
    },


    remove: removeLead,

    /**
     * Records a consent decision: the state with its date and how, the two
     * switches kept in step (consentWrite), and a line in the history.
     */
    setConsent: async (leadId: string, consent: Consent, actor: Actor): Promise<void> => {
      const home = await homeOf(leadId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, leadId), { ...consentWrite(consent), updatedAt: serverTimestamp() });
      batch.set(newActivityRef(home, leadId), activityEntry(actor, "note", { text: `Consent: ${describeConsent(consent)}` }));
      await batch.commit();
    },

    /**
     * "Remove this person's details". A lead is deleted with its history,
     * audits and time logs. Someone who said stop keeps a bare record (name,
     * phone, email, "said stop") on the do-not-contact list, because a Maps
     * search would otherwise add them again and they would be contacted.
     */
    removeDetails: async (lead: Lead): Promise<void> => {
      const home = await homeOf(lead.id);
      await removeLead(lead.id);
      if (lead.consent.state !== "stopped") return;
      await setDoc(doc(getDb(), home, lead.id), {
        ...(home === BUSINESSES ? newBusinessFields(lead.id, lead.name) : {}),
        name: lead.name,
        phone: lead.phone,
        email: lead.email,
        source: lead.source,
        stage: "lost",
        lostReason: "Said stop",
        doNotContact: true,
        marketingConsent: false,
        consent: lead.consent,
        parked: false,
        contacts: [],
        branches: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        stageChangedAt: serverTimestamp(),
      });
    },

    /** Everything held about a lead, for a data request: the record, history and time and costs. */
    exportData: async (leadId: string): Promise<Record<string, unknown>> => {
      const home = await homeOf(leadId);
      const record = await getDoc(doc(getDb(), home, leadId));
      const history = await getDocs(collection(getDb(), activityPath(home, leadId)));
      const effort = await Promise.all(
        [FIRESTORE_COLLECTIONS.timeEntries, FIRESTORE_COLLECTIONS.costEntries].map((name) =>
          getDocs(query(collection(getDb(), name), where("recordKind", "==", "lead"), where("recordId", "==", leadId))),
        ),
      );
      return {
        exportedAt: new Date().toISOString(),
        lead: { id: leadId, ...(record.data() ?? {}) },
        history: history.docs.map((d) => ({ id: d.id, ...d.data() })),
        timeEntries: effort[0].docs.map((d) => ({ id: d.id, ...d.data() })),
        costEntries: effort[1].docs.map((d) => ({ id: d.id, ...d.data() })),
      };
    },
  };
};

export const leadsRepo = createLeadsRepo(() => db);

export type LeadsRepo = ReturnType<typeof createLeadsRepo>;
